/* global google */
const domFunctions = require('../utils/_domFunctions');
const urlUtils = require('../utils/_urlUtils');

const MIN_LETTERS = 3;

const rad = function (x) {
    return (x * Math.PI) / 180;
};

const getDistance = function (p1, p2) {
    const R = 6378137; // Earth’s mean radius in meter
    const dLat = rad(p2.latitude - p1.latitude);
    const dLong = rad(p2.longitude - p1.longitude);
    const a = (Math.sin(dLat / 2) * Math.sin(dLat / 2)) + (
        Math.cos(rad(p1.latitude)) * Math.cos(rad(p2.latitude))
      * Math.sin(dLong / 2) * Math.sin(dLong / 2));
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c;
    return d; // returns the distance in meter
};

class mStoresSearch extends HTMLInputElement {
    constructor() {
        super();
        this.googlePlaces = null;
        this.googleGeocoder = null;
        this.suggestions = this.parentNode.querySelector('#search-store-suggestions');
        this.suggestionsStatus = this.parentNode.querySelector('#search-store-suggestions-status');
        this.timeout = null;
        this.activeIndex = -1;
        this.getSuggestions = this.getSuggestions.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.home = Object.hasOwnProperty.call(this.dataset, 'storelocatorHome');
        this.storeLocator = this.closest('[is="o-store-locator"]');
        this.specialRadiusTypes = ['administrative_area_level_1', 'administrative_area_level_2', 'colloquial_area', 'country']; // Countries and states
        this.cityTypes = ['administrative_area_level_3', 'locality']; // Cities
        this.preciseTypes = ['street_address', 'route', 'intersection', 'neighborhood', 'premise', 'subpremise', 'postal_code'];
    }

    connectedCallback() {
        this.initServices();
        this.addEventListener('input', () => {
            if (this.value.length >= MIN_LETTERS) {
                if (this.timeout) {
                    clearTimeout(this.timeout);
                }
                this.timeout = setTimeout(this.getSuggestions, 250);
            } else {
                this.clearSuggestions();
            }
        });
        this.addEventListener('keydown', this.onKeydown);
        this.storeLocator.dispatchEvent(new CustomEvent('storelocator:initSearch'));
    }

    onKeydown(e) {
        const options = Array.from(this.suggestions.children);
        if (!options.length) {
            return;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                this.setActiveOption(Math.min(this.activeIndex + 1, options.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                this.setActiveOption(Math.max(this.activeIndex - 1, 0));
                break;
            case 'Enter': {
                e.preventDefault();
                const suggestion = options[this.activeIndex] || options[0];
                suggestion.dispatchEvent(new Event('click'));
                break;
            }
            case 'Escape':
                e.preventDefault();
                this.clearSuggestions();
                break;
            default:
                break;
        }
    }

    setActiveOption(index) {
        const options = Array.from(this.suggestions.children);
        options.forEach((option) => option.setAttribute('aria-selected', 'false'));
        this.activeIndex = index;
        const active = options[index];
        active.setAttribute('aria-selected', 'true');
        this.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
    }

    clearSuggestions() {
        if (this.suggestions.innerText) {
            this.activeIndex = -1;
            this.removeAttribute('aria-activedescendant');
            this.setAttribute('aria-expanded', false);
            this.suggestions.classList.remove('displaying-suggestions');
            domFunctions.emptyNode(this.suggestions);
            this.suggestionsStatus.innerText = '';
        }
    }

    getSuggestions() {
        this.googlePlaces.getPlacePredictions({ input: this.value, types: ['geocode'] }, (results, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
                const byGoogleAutoCompletion = [];
                for (let i = 0; i < results.length; i++) {
                    byGoogleAutoCompletion.push({
                        address: results[i].description,
                        placeId: results[i].place_id,
                        isCountry: results[i].types[0] === 'country',
                        isCity: this.cityTypes.includes(results[i].types[0]),
                        isSpecialRadius: this.specialRadiusTypes.includes(results[i].types[0]),
                        isPreciseType: this.preciseTypes.includes(results[i].types[0]),
                    });
                }
                this.showAutoCompletion(byGoogleAutoCompletion);
            }
        });
    }

    showAutoCompletion(predictions) {
        domFunctions.emptyNode(this.suggestions);
        this.activeIndex = -1;
        this.removeAttribute('aria-activedescendant');

        if (predictions.length) {
            // show results
            this.setAttribute('aria-expanded', true);
            this.suggestions.classList.add('displaying-suggestions');
            predictions.forEach((result) => {
                const suggestion = document.createElement('li');
                suggestion.setAttribute('role', 'option');
                suggestion.setAttribute('aria-selected', 'false');
                suggestion.setAttribute('id', `result-suggestion-${predictions.indexOf(result)}`);
                suggestion.dataset.placeId = result.placeId;
                suggestion.dataset.isSpecialRadius = result.isSpecialRadius;
                suggestion.dataset.isPreciseType = result.isPreciseType;
                const eltText = document.createElement('span');
                eltText.innerText = result.address;
                suggestion.appendChild(eltText);
                suggestion.addEventListener('click', () => {
                    this.value = result.address;
                    this.search(suggestion.dataset.placeId, result.isCity, result.isSpecialRadius, result.isPreciseType, result.isCountry);
                });
                this.suggestions.appendChild(suggestion);
            });
        }
        const label = predictions.length > 1
            ? this.suggestionsStatus.dataset.i18nLabelPlural
            : this.suggestionsStatus.dataset.i18nLabelSingular;
        this.suggestionsStatus.innerText = label.replace('{0}', predictions.length);
    }

    search(placeId, isCity, isSpecialRadius, isPreciseType, isCountry) {
        this.clearSuggestions();
        let url = this.dataset.searchUrl;
        this.googleGeocoder.geocode({ placeId }, (results, status) => {
            if (status === 'OK') {
                const { viewport } = results[0].geometry;
                let radius;
                if (viewport && isSpecialRadius) { // Take half of the viewport size has a radius for the search in backend when it is a country or a state
                    const viewportKeys = Object.keys(viewport);
                    const p1 = { latitude: viewport[viewportKeys[0]].hi, longitude: viewport[viewportKeys[1]].hi };
                    const p2 = { latitude: viewport[viewportKeys[0]].lo, longitude: viewport[viewportKeys[1]].lo };
                    radius = getDistance(p1, p2) / 2;
                }
                const lat = results[0].geometry.location.lat();
                const long = results[0].geometry.location.lng();
                url = urlUtils.appendToUrl(url, {
                    lat,
                    long,
                    placeId,
                    input: this.value,
                    sort: isPreciseType ? 'position' : 'name',
                    country: isCountry,
                });
                if (radius) {
                    url = urlUtils.appendToUrl(url, {
                        radius: Math.round(radius),
                    });
                }
                if (this.home) {
                    window.location = url;
                } else {
                    this.storeLocator.dispatchEvent(new CustomEvent('storelocator:search', {
                        detail: {
                            url,
                            params: {
                                lat,
                                long,
                                placeId,
                                input: this.value,
                                radius: Math.round(radius) || '',
                                sort: isPreciseType ? 'position' : 'name',
                                country: isCountry,
                            },
                            viewport: isCity ? null : viewport, // In case of a city, the radisu of zoom is always 78km, in other case we take the map viewport recommended
                        },
                    }));
                }
            }
        });
    }

    initServices() {
        this.googlePlaces = new google.maps.places.AutocompleteService();
        this.googleGeocoder = new google.maps.Geocoder();
    }
}

customElements.define('m-stores-search', mStoresSearch, {
    extends: 'input',
});
